import { useCallback, useSyncExternalStore } from "react";
import { type QueryClient, type QueryKey, useQueryClient } from "@tanstack/react-query";
import { FEATURE_STATUS_RERUNNING } from "config";
import { projectKeys } from "queries/project.keys";
import type { FeatureStatusPayload } from "hooks/useProjects/useProjects.types";
import { isActiveAiRunStatus } from "utils/aiRunTransitions";

/**
 * Whether this document's budget run is active, for the re-run guard, the assumptions dialog
 * and the targeted rebudget banner. Two sources; neither reads the client's wall clock:
 *
 * 1. Cached status rows. The project detail and the document-scoped detail can both hold this
 *    document's budget row. The row with the latest server `updated_at` decides; ties go to the
 *    active status. Unrelated writes to a project cache never change a row's server time.
 * 2. A pending re-run marker, set only when a re-run POST succeeds. It bridges the gap until a
 *    cache shows the run. It ends when a cached row for this document is terminal with a server
 *    time at or after the run's server start, or when the run's length has passed locally.
 *
 * A 409 sets no marker: it refreshes the caches and the returned server status decides.
 * The API's 409 stays the final safety net.
 */

type DocumentId = string | number | null | undefined;
/** The API returns every row column, including the server write time. */
type BudgetStatusRow = FeatureStatusPayload & { updated_at?: string | null };
/** Fields read from the re-run response (`AiRunStartReceipt`). */
type RunStartReceipt = {
  run_id?: unknown;
  started_at?: unknown;
  expected_timeout_at?: unknown;
  status?: unknown;
};

/** What a successful re-run response says about the run it started. */
export type BudgetRunStart = {
  /** The run's id; terminal status rows keep it in `metadata.run_id`. */
  runId: string | null;
  /** Server start time as sent; status rows keep it in `metadata.started_at`. */
  startedAt: string;
  serverStartedAtMs: number;
  /** The server's run length (never its deadline, so clock skew cannot shift it). */
  durationMs: number;
  /** The run's status when the server replied, if sent. */
  status: string | null;
};

/** Reads the start receipt from a re-run response body. Null without a readable `started_at`. */
export const readBudgetRunStart = (response: unknown): BudgetRunStart | null => {
  const body = response as { data?: RunStartReceipt } | RunStartReceipt | undefined;
  const receipt: RunStartReceipt | undefined =
    body && "data" in body && body.data ? body.data : (body as RunStartReceipt | undefined);
  const serverStartedAtMs = serverTime(receipt?.started_at);
  if (!serverStartedAtMs) return null;
  const timeoutAtMs = serverTime(receipt?.expected_timeout_at);
  return {
    runId: typeof receipt?.run_id === "string" && receipt.run_id ? receipt.run_id : null,
    startedAt: receipt?.started_at as string,
    serverStartedAtMs,
    durationMs:
      timeoutAtMs > serverStartedAtMs ? timeoutAtMs - serverStartedAtMs : BUDGET_RUN_TIMEOUT_MS,
    status: typeof receipt?.status === "string" ? receipt.status : null,
  };
};

/** Backend DEFAULT_FEATURE_RUN_TIMEOUT_MS for budget, used when the response has no timeout. */
export const BUDGET_RUN_TIMEOUT_MS = 25 * 60 * 1000;

const hasDocumentScope = (documentId: DocumentId): documentId is string | number =>
  documentId != null && String(documentId) !== "" && String(documentId) !== "0";

/** A budget row for this document. Untagged rows count; rows for another document do not. */
const isBudgetRowFor = (row: FeatureStatusPayload, documentId: DocumentId) =>
  row.feature === "budget" &&
  (row.document_id == null || String(row.document_id) === String(documentId));

const statusQueryKeys = (projectId: string | number, documentId: DocumentId) => {
  const keys: QueryKey[] = [projectKeys.detail(projectId)];
  if (hasDocumentScope(documentId)) {
    keys.push(projectKeys.detailForDocument(projectId, documentId));
  }
  return keys;
};

/** Server time in ms, or 0 when missing or unreadable. */
const serverTime = (value: unknown): number => {
  const ms = typeof value === "string" ? Date.parse(value) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

/** The cached project entry that holds this document's own status rows. */
export const budgetRunStatusQueryKey = (
  projectId: string | number,
  documentId: DocumentId
): QueryKey =>
  hasDocumentScope(documentId)
    ? projectKeys.detailForDocument(projectId, documentId)
    : projectKeys.detail(projectId);

/**
 * Copies an accepted re-run's receipt into the document's cached status row, so every reader of
 * that cache (the global AI run watcher's scan included) sees the run at once, as if a poll had
 * landed it. Not optimistic: the server writes this "rerunning" row before it replies.
 *
 * Only an existing cached project entry is changed. A row that already has this run, or a later
 * one, is newer than the receipt and is left alone. The next refetch replaces the copy. Cancel
 * this entry's fetches first (budgetRunStatusQueryKey), so an older answer cannot land on top.
 */
export const mirrorBudgetRunReceipt = (
  queryClient: QueryClient,
  projectId: string | number,
  documentId: DocumentId,
  response: unknown
) => {
  const start = readBudgetRunStart(response);
  const status = start?.status ?? FEATURE_STATUS_RERUNNING;
  if (!start || !isActiveAiRunStatus(status)) return;
  const key = budgetRunStatusQueryKey(projectId, documentId);

  queryClient.setQueryData<{ feature_statuses?: BudgetStatusRow[] } | null>(key, (cached) => {
    if (!cached) return cached;
    const rows = cached.feature_statuses ?? [];
    const index = rows.findIndex((row) => isBudgetRowFor(row, documentId));
    const current = index >= 0 ? rows[index] : undefined;
    const meta = (current?.metadata ?? {}) as { run_id?: unknown; started_at?: unknown };
    if (start.runId && meta.run_id === start.runId) return cached;
    if (serverTime(meta.started_at) >= start.serverStartedAtMs) return cached;

    const mirrored: BudgetStatusRow = {
      feature_status_id: current?.feature_status_id ?? 0,
      document_id:
        current?.document_id ?? (hasDocumentScope(documentId) ? String(documentId) : null),
      feature: "budget",
      status,
      metadata: start.runId
        ? { run_id: start.runId, started_at: start.startedAt }
        : { started_at: start.startedAt },
      updated_at: start.startedAt,
    };
    const next =
      index >= 0 ? rows.map((row, i) => (i === index ? mirrored : row)) : [...rows, mirrored];
    return { ...cached, feature_statuses: next };
  });
};

const cachedBudgetRows = (
  queryClient: QueryClient,
  projectId: string | number,
  documentId: DocumentId
): BudgetStatusRow[] =>
  statusQueryKeys(projectId, documentId).flatMap((key) =>
    (
      queryClient.getQueryData<{ feature_statuses?: BudgetStatusRow[] } | null>(key)
        ?.feature_statuses ?? []
    ).filter((row) => isBudgetRowFor(row, documentId))
  );

// Pending re-run markers, one per document. Module level so they outlive the dialog and are
// never garbage collected mid-run (an unobserved React Query entry is dropped after 5 minutes).
type PendingRerun = {
  /** Server start time, compared only with server row times. */
  serverStartedAtMs: number;
  /** Local monotonic start (performance.now), so wall clock changes cannot move the expiry. */
  localStartedAtMs: number;
  durationMs: number;
  timer: ReturnType<typeof setTimeout>;
};
const pendingReruns = new Map<string, PendingRerun>();
const listeners = new Set<() => void>();
const emit = () => {
  for (const listener of listeners) listener();
};
const markerId = (projectId: string | number, documentId: DocumentId) =>
  `${projectId}:${documentId ?? ""}`;

const dropMarker = (id: string) => {
  const marker = pendingReruns.get(id);
  if (!marker) return false;
  clearTimeout(marker.timer);
  pendingReruns.delete(id);
  return true;
};

const hasExpired = (marker: PendingRerun) =>
  performance.now() - marker.localStartedAtMs >= marker.durationMs;

/**
 * Records that the API started this document's budget run, from the successful re-run response
 * body. Without a readable `started_at` no marker is set, and the cached rows alone decide.
 */
export const markBudgetRerunPending = (
  projectId: string | number,
  documentId: DocumentId,
  response: unknown
) => {
  const start = readBudgetRunStart(response);
  if (!start) return;
  const { serverStartedAtMs, durationMs } = start;

  const id = markerId(projectId, documentId);
  dropMarker(id);
  const timer = setTimeout(() => {
    if (pendingReruns.get(id)?.timer === timer && dropMarker(id)) emit();
  }, durationMs);
  pendingReruns.set(id, {
    serverStartedAtMs,
    localStartedAtMs: performance.now(),
    durationMs,
    timer,
  });
  emit();
};

/** Removes this document's pending re-run marker, if any. */
export const clearBudgetRerunPending = (projectId: string | number, documentId: DocumentId) => {
  if (dropMarker(markerId(projectId, documentId))) emit();
};

export const isBudgetRunActiveInCache = (
  queryClient: QueryClient,
  projectId: string | number,
  documentId: DocumentId
): boolean => {
  const rows = cachedBudgetRows(queryClient, projectId, documentId);
  const freshest = rows.reduce<BudgetStatusRow | undefined>((best, row) => {
    if (!best) return row;
    const diff = serverTime(row.updated_at) - serverTime(best.updated_at);
    return diff > 0 || (diff === 0 && isActiveAiRunStatus(row.status)) ? row : best;
  }, undefined);
  if (isActiveAiRunStatus(freshest?.status)) return true;

  const id = markerId(projectId, documentId);
  const marker = pendingReruns.get(id);
  if (!marker) return false;
  const ended =
    hasExpired(marker) ||
    rows.some(
      (row) =>
        !isActiveAiRunStatus(row.status) && serverTime(row.updated_at) >= marker.serverStartedAtMs
    );
  // No emit: this read already answers "not active", so every reader agrees.
  if (ended) dropMarker(id);
  return !ended;
};

/** Re-renders when a project cache or a pending marker changes. Never fetches. */
export const useBudgetRunActive = (projectId: string | number, documentId: DocumentId): boolean => {
  const queryClient = useQueryClient();
  const subscribe = useCallback(
    (onChange: () => void) => {
      const unsubscribeCache = queryClient.getQueryCache().subscribe(onChange);
      listeners.add(onChange);
      return () => {
        unsubscribeCache();
        listeners.delete(onChange);
      };
    },
    [queryClient]
  );
  return useSyncExternalStore(subscribe, () =>
    isBudgetRunActiveInCache(queryClient, projectId, documentId)
  );
};
